// VerEstadoModal.tsx

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';

interface EstadoFirmante {
  cedula?: string;
  tipo?: string;
  orden?: number | null;
  nombres?: string;
  apellidos?: string;
  correo?: string;
  estado?: string;
  fechaFirma?: string | null;
  fechaCaducidadLink?: string | null;
}

interface EstadoDato {
  codigo?: string;
  noTramite?: string;
  totalFirmantes?: number;
  firmados?: number;
  pendientes?: number;
  completado?: boolean;
  firmantes?: EstadoFirmante[];
}

export interface VerEstadoModalProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  loading?: boolean;
  noTramite: string;
  estado?: { estado?: string; dato?: unknown };
  error?: string;
}

const ESTADO_COLOR: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  completado: 'success',
  firmado: 'success',
  enviado: 'info',
  pendiente: 'warning',
  caducado: 'error',
  cancelado: 'error',
};

const estadoChip = (estado: string | undefined): { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' } => {
  if (!estado) return { label: 'Sin estado', color: 'default' };
  const lower = estado.toLowerCase();
  const color = ESTADO_COLOR[lower] ?? 'default';
  return { label: estado, color };
};

const resolveEstadoGeneral = (info: { estado?: string; dato?: unknown } | undefined): string | undefined => {
  if (info?.estado) return info.estado;

  const dato = info?.dato as EstadoDato | null | undefined;
  if (!dato) return undefined;

  if (dato.completado) return 'Completado';
  if (typeof dato.pendientes === 'number' && dato.pendientes > 0) return 'En proceso';
  return undefined;
};

const primerasMayusculas = (texto?: string): string => {
  if (!texto) return '';
  return texto
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
};

export const VerEstadoModal = ({
  open,
  onClose,
  onRefresh,
  loading,
  noTramite,
  estado,
  error,
}: VerEstadoModalProps) => {
  const dato = estado?.dato as EstadoDato | null | undefined;
  const firmantes = Array.isArray(dato?.firmantes) ? dato.firmantes : [];
  const total = dato?.totalFirmantes ?? firmantes.length;
  const firmados = dato?.firmados ?? firmantes.filter((f) => (f.estado ?? '').toLowerCase() === 'firmado').length;

  const estadoGeneral = resolveEstadoGeneral(estado);
  const chip = estadoChip(estadoGeneral);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Estado en vivo del trámite {noTramite}
        <IconButton aria-label="Cerrar" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Consultando estado en Enext...
          </Alert>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && estado && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body1">Estado:</Typography>
              <Chip
                label={chip.label}
                color={chip.color}
                size="small"
                variant={chip.color === 'default' ? 'outlined' : 'filled'}
              />
            </Stack>

            {firmantes.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Progreso de firmas:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'action.hover',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: total > 0 ? `${(firmados / total) * 100}%` : '0%',
                        height: '100%',
                        bgcolor: 'success.main',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {firmados}/{total} firmados
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                {firmantes.map((f, i) => {
                  const fc = estadoChip(f.estado);
                  return (
                    <Stack key={i} spacing={0.5} sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonIcon color="action" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {primerasMayusculas(f.nombres)} {primerasMayusculas(f.apellidos)}
                        </Typography>
                        <Chip label={fc.label} color={fc.color} size="small" variant={fc.color === 'default' ? 'outlined' : 'filled'} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                        Cédula: {f.cedula ?? '-'}
                        {f.tipo ? ` · ${f.tipo}` : ''}
                        {typeof f.orden === 'number' ? ` · Orden ${f.orden}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                        Correo: {f.correo ?? '-'}
                      </Typography>
                      {f.fechaFirma && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                          Firmado el: {f.fechaFirma}
                        </Typography>
                      )}
                      {f.fechaCaducidadLink && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                          Caducidad de link: {f.fechaCaducidadLink}
                        </Typography>
                      )}
                      {i < firmantes.length - 1 && <Divider sx={{ mt: 1 }} />}
                    </Stack>
                  );
                })}
              </Box>
            )}

            {firmantes.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Sin información de firmantes en el estado del trámite.
              </Typography>
            )}
          </Stack>
        )}

        {!loading && !error && !estado && (
          <Alert severity="warning">
            No hay información del estado para este trámite.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {onRefresh && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refrescar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VerEstadoModal;
